import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_BASE } from '../services/api';
import { FiActivity, FiUsers, FiTrendingUp, FiRefreshCw, FiCheck, FiX } from 'react-icons/fi';
import './GlobalAudit.css';

function GlobalAudit() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    loadGlobalStats();
  }, []);

  const loadGlobalStats = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateRange.startDate) params.startDate = dateRange.startDate;
      if (dateRange.endDate) params.endDate = dateRange.endDate;

      const response = await axios.get(`${API_BASE}/api/audit/global/stats`, { params });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading global stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (e) => {
    setDateRange({
      ...dateRange,
      [e.target.name]: e.target.value
    });
  };

  const applyFilter = () => {
    loadGlobalStats();
  };

  const getActionLabel = (action) => {
    const labels = {
      session_start: t('globalAudit.actions.sessionStart'),
      session_end: t('globalAudit.actions.sessionEnd'),
      card_register: t('globalAudit.actions.cardRegister'),
      card_delete: t('globalAudit.actions.cardDelete'),
      data_save: t('globalAudit.actions.dataSave'),
      data_view: t('globalAudit.actions.dataView'),
      data_download: t('globalAudit.actions.dataDownload'),
      data_delete: t('globalAudit.actions.dataDelete')
    };
    return labels[action] || action;
  };

  return (
    <div className="global-audit">
      <div className="audit-header">
        <h2><FiActivity /> {t('globalAudit.title')}</h2>
        <button onClick={loadGlobalStats} className="btn-refresh" disabled={loading}>
          <FiRefreshCw className={loading ? 'spinning' : ''} />
          {t('globalAudit.refresh')}
        </button>
      </div>

      <div className="date-filter">
        <div className="filter-group">
          <label>{t('globalAudit.from')}</label>
          <input
            type="date"
            name="startDate"
            value={dateRange.startDate}
            onChange={handleDateChange}
          />
        </div>
        <div className="filter-group">
          <label>{t('globalAudit.to')}</label>
          <input
            type="date"
            name="endDate"
            value={dateRange.endDate}
            onChange={handleDateChange}
          />
        </div>
        <button onClick={applyFilter} className="btn-apply">
          {t('globalAudit.applyFilter')}
        </button>
      </div>

      {loading ? (
        <div className="loading-spinner">
          <FiRefreshCw className="spinning" size={48} />
          <p>{t('globalAudit.loadingStats')}</p>
        </div>
      ) : stats ? (
        <div className="stats-container">
          <div className="stats-section">
            <h3><FiTrendingUp /> {t('globalAudit.systemActions')}</h3>
            <div className="stats-grid">
              {stats.actionStats && stats.actionStats.length > 0 ? (
                stats.actionStats.map((stat, index) => (
                  <div key={index} className="stat-card">
                    <div className="stat-label">{getActionLabel(stat._id)}</div>
                    <div className="stat-value">{stat.count}</div>
                  </div>
                ))
              ) : (
                <p className="no-data">{t('globalAudit.noStats')}</p>
              )}
            </div>
          </div>

          <div className="stats-section">
            <h3><FiUsers /> {t('globalAudit.topCards')}</h3>
            <div className="top-cards">
              {stats.topCards && stats.topCards.length > 0 ? (
                <table className="cards-table">
                  <thead>
                    <tr>
                      <th>{t('globalAudit.col.rfidId')}</th>
                      <th>{t('globalAudit.col.totalActions')}</th>
                      <th>{t('globalAudit.col.lastActivity')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.topCards.map((card, index) => (
                      <tr key={index}>
                        <td className="rfid-cell">{card._id}</td>
                        <td className="count-cell">{card.totalActions}</td>
                        <td className="date-cell">
                          {new Date(card.lastActivity).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="no-data">{t('globalAudit.noCards')}</p>
              )}
            </div>
          </div>

          <div className="stats-section">
            <h3><FiActivity /> {t('globalAudit.recentActivity')}</h3>
            <div className="recent-activity-global">
              {stats.recentActivity && stats.recentActivity.length > 0 ? (
                <div className="activity-timeline">
                  {stats.recentActivity.map((activity, index) => (
                    <div key={index} className="timeline-item">
                      <div className="timeline-marker">
                        {activity.metadata?.success ? <FiCheck /> : <FiX />}
                      </div>
                      <div className="timeline-content">
                        <div className="timeline-action">{getActionLabel(activity.action)}</div>
                        <div className="timeline-rfid">{t('globalAudit.rfidPrefix', { id: activity.rfidId })}</div>
                        <div className="timeline-time">
                          {new Date(activity.timestamp).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-data">{t('globalAudit.noActivity')}</p>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default GlobalAudit;
